/* tslint:disable */

import { SketchfabClient, TextureQuailty, Animation, SketchfabClientOptions } from './SketchfabClient';
import * as Sketchfab from 'sketchfab';
import * as _ from 'lodash';
import * as Commands from './commands';
import * as utils from '@/utils/index';

type ProgressHandlerFunction = (isProcessing: boolean) => void;
const gamma: number = 2.4;


export class ConfiguratorServiceSettings {
  public modelId: string;
  public textureBaseUrl: string;
}

export type ObjectType = 'MatrixTransform' |'Geometry' | 'Group';

export class ConfiguratorService {
  private _nodes: NodeMap[];
  private _settings: ConfiguratorServiceSettings;
  private _progressHandler: ProgressHandlerFunction;
  private _textureCache: { [key: string]: string } = {};
  private _animationTimers: number[] = [];

  public get nodes(): NodeMap[] {
    return this._nodes;
  }

  public readonly Client: SketchfabClient;

  public constructor(client: SketchfabClient, settings: ConfiguratorServiceSettings) {
    this.Client = client;
    this._settings = settings;
  }

  public execute(type: string, params: any): Promise<any> {
    const dispatch: (command: Commands.ICommand) => Promise<any> = this[type];
    const commands: any = Commands;
    const commandName = `${_.upperFirst(type)}Command`;
    const ctor = commands[commandName];
    const command = params;
    const promise = dispatch.apply(this, [command]);
    return promise;
  }

  private async getTextureUid(texture: string): Promise<string> {
    const textureImage = texture;
    const url = _.startsWith(textureImage, 'http://') || _.startsWith(textureImage, 'https://') ? textureImage : this._settings.textureBaseUrl + textureImage;
    const cachedTextureUid = this._textureCache[url];
    if (cachedTextureUid != null) {
      return cachedTextureUid;
    }

    const uid = await this.Client.addTexture(url);
    this._textureCache[url] = uid;
    return uid;

  }

  private async setTextureChannel(channel: TextureMaterialChannel, command: Commands.ChangeTextureCommand): Promise<any> {
    if (!utils.isNullOrUndefined(command.value)) {
      channel.factor = parseFloat(command.value.toString());
    }

    if (!utils.isNullOrUndefined(command.enable)) {
      channel.enable = command.enable;
    }

    if (utils.isNullOrUndefined(command.texture)) {
      return;
    }

    // tslint:disable-next-line:strict-boolean-expressions
    channel.texture = channel.texture || {} as MaterialChannelTexture;
    const uid = await this.getTextureUid(command.texture);
    channel.texture.uid = uid;
  }

  public changeEmitColor(command: Commands.ChangeEmitColorCommand): Promise<any> {
    return Promise.all(_.map(command.materials, (mat) => {
      return this.changeMaterial(mat, (m) => {
        m.channels.EmitColor.color = ConfiguratorService.hexToLinear(command.color);
        m.channels.EmitColor.enable = true;
        m.channels.EmitColor.factor = 1;
        return;
      });
    }));
  }

  public disableEmitColor(command: Commands.DisableEmitColorCommand): Promise<any> {
    return Promise.all(_.map(command.materials, (mat) => {
      return this.changeMaterial(mat, (m) => {
        m.channels.EmitColor.enable = false;
      });
    }));
  }

  public changeOpacity(command: Commands.ChangeOpacityCommand): Promise<any> {
    return Promise.all(_.map(command.materials, (mat) => {
      return this.changeMaterial(mat, (m) => {
        return this.setTextureChannel(m.channels.Opacity, command);
      });
    }));
  }

  public changeCavity(command: Commands.ChangeCavityCommand): Promise<any> {
    return Promise.all(_.map(command.materials, (mat) => {
      return this.changeMaterial(mat, (m) => {
        return this.setTextureChannel(m.channels.CavityPBR, command);
      });
    }));
  }

  public changeAo(command: Commands.ChangeAoPBRCommand): Promise<any> {
    return Promise.all(_.map(command.materials, (mat) => {
      return this.changeMaterial(mat, (m) => {
        return this.setTextureChannel(m.channels.AOPBR, command);
      });
    }));
  }

  public changeDiffuse(command: Commands.ChangeDiffuseCommand): Promise<any> {
    return Promise.all(_.map(command.materials, (mat) => {
      return this.changeMaterial(mat, (m) => {
        return this.setTextureChannel(m.channels.DiffusePBR, command);
      });
    }));
  }

  public changeRoughness(command: Commands.ChangeRoughnessCommand): Promise<any> {
    return Promise.all(_.map(command.materials, (mat) => {
      return this.changeMaterial(mat, (m) => {
        return this.setTextureChannel(m.channels.RoughnessPBR, command);
      });
    }));
  }

  public changeGlossiness(command: Commands.ChangeRoughnessCommand): Promise<any> {
    return Promise.all(_.map(command.materials, (mat) => {
      return this.changeMaterial(mat, (m) => {
        return this.setTextureChannel(m.channels.GlossinessPBR, command);
      });
    }));
  }

  public changeAlbedoPBR(command: Commands.ChangeAlbedoPBRCommand) {
    return Promise.all(_.map(command.materials, (mat) => {
      return this.changeMaterial(mat, (m) => {
        if (command.color) {
          m.channels.AlbedoPBR.color = ConfiguratorService.hexToLinear(command.color);
        }
        return this.setTextureChannel(m.channels.AlbedoPBR, command);
      });
    }));
  }

  public changeBumpMap(command: Commands.ChangeBumpMapCommand): Promise<any> {
    return Promise.all(_.map(command.materials, (mat) => {
      return this.changeMaterial(mat, (m) => {
        return this.setTextureChannel(m.channels.BumpMap, command);
      });
    }));
  }

  public changeMetalness(command: Commands.ChangeMetalnessCommand): Promise<any> {
    return Promise.all(_.map(command.materials, (mat) => {
      return this.changeMaterial(mat, (m) => {
        return this.setTextureChannel(m.channels.MetalnessPBR, command);
      });
    }));
  }

  private changeMaterial(material: string, func: (mat: Material) => void | Promise<any> | boolean): Promise<any> {
    const m = this.Client.getMaterial(material);
    if (!m) {
      console.warn(`${material} not found!`);
      return Promise.resolve(true);
    }
    const r = func(m);
    if (this.isPromise(r)) {
      return r.then(() => {
        return this.Client.setMaterial(m);
      });
    }
    if (this.isBoolean(r)) {
      if (r) {
        return this.Client.setMaterial(m);
      }
    }
    return this.Client.setMaterial(m);
  }

  public hideObjects(command: Commands.HideObjectsCommand): Promise<any> {
    // tslint:disable-next-line:strict-boolean-expressions
    command.operator = command.operator || Commands.FilterOperators.Equal;
    command.trim = command.trim || false;

    let objects = command.objects;
    if (command.trim) {
      objects = _.map(objects, o => _.trim(o));
    }

    const nodes = _.filter(this._nodes, (x: NodeMap) => {
      const name = command.trim ? _.trim(x.name) : x.name;
      const r = _.map(objects, o => this.filter(command.operator, o, name));

      // tslint:disable-next-line:strict-boolean-expressions
      return x.name && !x.localMatrix && _.some(r);
    });
    const def = [];
    return Promise.race(_.map(nodes, (n: NodeMap) => {
      return this.Client.hideObject(n.instanceID);
    }));
  }

  public showObjects(command: Commands.ShowObjectsCommand): Promise<any> {
    // tslint:disable-next-line:strict-boolean-expressions
    command.operator = command.operator || Commands.FilterOperators.Equal;
    command.trim = command.trim || false;

    let objects = command.objects;
    if (command.trim) {
      objects = _.map(objects, o => _.trim(o));
    }

    const nodes = _.filter(this._nodes, (x: NodeMap) => {
      const name = command.trim ? _.trim(x.name) : x.name;
      const r = _.map(objects, o => this.filter(command.operator, o, name));

      return x.name && !x.localMatrix && _.some(r);
    });
    const def = [];
    return Promise.race(_.map(nodes, (n: NodeMap) => {
      return this.Client.showObject(n.instanceID);
    }));
  }

  public registerProgressHandlerFunc(func: ProgressHandlerFunction) {
    this._progressHandler = func;
  }

  public startProcessing() {
    if (!this._progressHandler) {
      return;
    }
    this._progressHandler(true);
  }

  public stopProcessing() {
    if (!this._progressHandler) {
      return;
    }
    this._progressHandler(false);
  }

  public static async init(iframe:Element,settings: ConfiguratorServiceSettings): Promise<ConfiguratorService> {
    const client = new SketchfabClient(iframe, settings.modelId);
    const options = new SketchfabClientOptions();
    options.autospin = 0;
    options.autostart = 1;
    options.cardboard = 0;
    options.ui_infos = 0;
    options.ui_hint = 1;
    options.ui_vr = 0;
    options.ui_animations = 0;
    options.ui_settings = 0;
    options.ui_annotations = 0;
    options.ui_help = 0;
    options.ui_fullscreen = 0;
    options.annotations_visible = 0;
    options.camera = 1;
    options.ui_stop = 0;
    options.ui_controls = 0;
    options.transparent = 0;
    options.ui_watermark = 0;
    options.ui_inspector = 0;

    const r = await client.init(options);

    (window as any).x = client;
    const service = new ConfiguratorService(client, settings);
    service._nodes = await client.getNodeMap();
    return service;
  }

  public stopAllAnimations() {
    this.Client.pauseAnimation();
    this.Client.seekAnimationTo(0);
    _.each(this._animationTimers, (t) => {
      clearTimeout(t);
    });
    this._animationTimers = [];
  }

  public async pauseAnimations(): Promise<any> {
    await this.Client.pauseAnimation();
    //    await this.Client.seekAnimationTo(0);
  }

  public async playPauseAnimationInSecond(name: string, second: number): Promise<any> {
    const an = _.find(this.Client.animations, a => a.name === name);
    const duration = second * 1000;

    await this.Client.pauseAnimation();
    await this.Client.setAnimation(an.uid);
    _.each(this._animationTimers, (t) => {
      clearTimeout(t);
    });
    this._animationTimers = [];

    await this.Client.playAnimation();
    await this.Client.setAnimation(an.uid);

    return new Promise<void>((resolve, error) => {
      const ti = window.setTimeout(() => {
        this.Client.pauseAnimation();
        _.each(this._animationTimers, (t) => {
          clearTimeout(t);
        });
        this._animationTimers = [];
        resolve();
      }, duration);
      this._animationTimers.push(ti);
    });
  }

  public async playPauseAnimation(name: string, durationFunc: (duration: number) => number = null): Promise<any> {

    let func = durationFunc;
    if (durationFunc == null) {
      func = func = d => d * 1000;
    }

    const an = _.find(this.Client.animations, a => a.name === name);
    const duration = func(an.duration);

    await this.Client.pauseAnimation();
    await this.Client.setAnimation(an.uid);
    _.each(this._animationTimers, (t) => {
      clearTimeout(t);
    });

    this._animationTimers = [];

    await this.Client.playAnimation();
    await this.Client.setAnimation(an.uid);

    return new Promise<void>((resolve, error) => {
      const ti = window.setTimeout(() => {
        this.Client.pauseAnimation();
        _.each(this._animationTimers, (t) => {
          clearTimeout(t);
        });
        this._animationTimers = [];
        resolve();
      }, duration);
      this._animationTimers.push(ti);
    });
  }

  public gotoAnnotation(index: number, onFocus?: () => void, onBlur?: () => void): Promise<any> {
    const i = index - 1;

    if (onFocus) {
      const subscriptionName4Focus = `subscription::ann::${index}::focus`;
      this.Client.subscribeToAnnotationFocus(subscriptionName4Focus, (a, ind) => {
        if (ind === i) {
          onFocus();
          this.Client.unsubscribeFromAnnotationFocus(subscriptionName4Focus);
        }
      });
    }

    if (onBlur) {
      const subscriptionName4Blur = `subscription::ann::${index}::blur`;
      this.Client.subscribeToAnnotationBlur(subscriptionName4Blur, (a, ind) => {
        if (ind === i) {
          onBlur();
          this.Client.unsubscribeFromAnnotationBlur(subscriptionName4Blur);
        }
      });
    }

    return this.Client.gotoAnnotation(index - 1);
  }

  private isPromise(arg: any): arg is Promise<boolean> {
    return arg !== undefined && arg.then;
  }

  private isBoolean(arg: any): arg is boolean {
    return arg !== undefined;
  }

  public filter(operator: Commands.FilterOperators = Commands.FilterOperators.Equal, objectName: string, realObjectName: string): boolean {
    if (_.includes(objectName, '*')) {
      return true;
    }
    if (operator === Commands.FilterOperators.Equal) {
      return objectName === realObjectName;
    }

    if (operator === Commands.FilterOperators.Contains) {
      return _.includes(realObjectName, objectName);
    }

    if (operator === Commands.FilterOperators.StarsWith) {
      return _.startsWith(realObjectName, objectName);
    }

    if (operator === Commands.FilterOperators.EndsWith) {
      return _.endsWith(realObjectName, objectName);
    }

    if (operator === Commands.FilterOperators.Regex) {
      const re = new RegExp(objectName);
      return re.test(realObjectName);
    }

    return false;
  }

  static linearToSrgb(linear: number) {
    let v = 0.0;
    if (linear < 0.0031308) {
      if (linear > 0.0) {
        v = linear * 12.92;
      }
    } else {
      v = 1.055 * Math.pow(linear, 1.0 / gamma) - 0.055;
    }
    return v;
  }

  static srgbToLinear(sgrb: number) {
    let v = 0.0;
    if (sgrb < 0.04045) {
      if (sgrb >= 0.0) {
        v = sgrb * (1.0 / 12.92);
      }
    } else {
      v = Math.pow((sgrb + 0.055) * (1.0 / 1.055), gamma);
    }
    return v;
  }

  static hexToRgb(hex: string): { r: number, g: number, b: number } {
    // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    // tslint:disable-next-line:no-parameter-reassignment
    hex = hex.replace(shorthandRegex, (m, r, g, b) => {
      return r + r + g + g + b + b;
    });

    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16),
    } : null;
  }

  static hexToLinear(hex: string): Float64Array {
    const rgb = this.hexToRgb(hex);
    if (rgb == null) {
      return null;
    }

    const r = this.srgbToLinear(rgb.r / 255);
    const g = this.srgbToLinear(rgb.g / 255);
    const b = this.srgbToLinear(rgb.b / 255);
    return new Float64Array([r, g, b]);
  }

}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/services/sketchfab/service.ts